import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@/redux/hooks/hooks";
import { computeProfileCompletion, pickEmoji } from "@/utils/profileCompletion";

function SkeletonLine({ w = "w-24" }: { w?: string }) {
  return <div className={`h-3 ${w} rounded-full bg-[#EDEDED] animate-pulse`} />;
}

function isSliceReady(slice: any) {
  const info = slice?.SubmitApplicationInfo;
  const v2 = slice?.v2Extras;
  if (!info || !v2) return false;

  const hasName =
    typeof info.fullName === "string" && info.fullName.trim().length > 0;
  const hasPhoto = !!v2.profilePhoto;
  const hasGender =
    typeof info.gender === "string" && info.gender.trim().length > 0;

  return hasName || hasPhoto || hasGender;
}

export default function ProfileCompletionTopPopup({
  currentStep,
  onOpen,
}: {
  currentStep: number;
  onOpen: () => void;
}) {
  const slice = useAppSelector((s) => s.signUpV2);
  const { percent } = useMemo(() => computeProfileCompletion(slice), [slice]);
  const emoji = useMemo(
    () => pickEmoji(percent, currentStep),
    [percent, currentStep],
  );

  const ready = isSliceReady(slice);
  const [showSkeleton, setShowSkeleton] = useState(true);
  useEffect(() => {
    if (ready) {
      const t = setTimeout(() => setShowSkeleton(false), 250);
      return () => clearTimeout(t);
    }
    setShowSkeleton(true);
  }, [ready]);

  const info = slice?.SubmitApplicationInfo;
  const v2 = slice?.v2Extras;

  const displayName = (info?.fullName && info.fullName.trim()) || "—";
  const photoUrl =
    v2?.profilePhoto?.live_url || v2?.profilePhoto?.local_url || "";

  // smooth entrance
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        "w-full md:w-[min(560px,calc(100%-24px))]",
        "border border-[#D8D8DD] rounded-2xl bg-white backdrop-blur",
        "shadow-[0_10px_30px_rgba(0,0,0,0.10)]",
        "px-4 py-3",
        "transition-all duration-500 ease-out",
        mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2",
      ].join(" ")}
      aria-label="Open profile completion details"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="size-[40px] rounded-full bg-[#EDEDED] overflow-hidden shrink-0">
            {showSkeleton ? null : photoUrl ? (
              <img
                src={photoUrl}
                alt="profile"
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>

          <div className="min-w-0">
            {showSkeleton ? (
              <div className="space-y-2">
                <SkeletonLine w="w-40" />
                <SkeletonLine w="w-24" />
              </div>
            ) : (
              <>
                <p className="text-sm tracking-[-0.7px] font-medium text-[#060B13] truncate text-start">
                  {displayName}
                </p>
                <p className="text-[11px] tracking-[-0.7px] text-[#A6A6A6] font-medium">
                  Tap to complete profile
                </p>
              </>
            )}
          </div>
        </div>

        <div className="shrink-0 w-10 h-10 rounded-full bg-[#FFF4EC] flex items-center justify-center text-xl">
          {emoji}
        </div>
      </div>

      {/* progress (compact) */}
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <p className="text-[#060B13] font-medium text-[12px] tracking-[-0.7px]">
            {percent}% complete
          </p>
          <span className="text-[11px] text-[#A6A6A6] font-medium">View</span>
        </div>

        <div className="mt-2 h-[7px] w-full rounded-[21px] bg-[#FF66001A] overflow-hidden">
          <div
            className="h-full rounded-[21px] bg-[#FF6600] transition-[width] duration-700 ease-out will-change-[width]"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </button>
  );
}
